import { useState, useEffect } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Activity, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';

const EMPTY = { codigo: '', nombre: '', regular: true, qrs: 'angosto', fc_min: 0, fc_max: 300, activo: true };

export default function EcgRhythmsPage() {
  const [rhythms, setRhythms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/ecg-rhythms').then(r => setRhythms(r.data || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing('new'); };
  const openEdit = (r) => { setForm({ ...r }); setEditing(r.id); };

  const save = async () => {
    if (!form.codigo || !form.nombre) { toast.error('Código y nombre son obligatorios'); return; }
    setSaving(true);
    try {
      const payload = { ...form, fc_min: parseInt(form.fc_min) || 0, fc_max: parseInt(form.fc_max) || 300 };
      if (editing === 'new') await api.post('/ecg-rhythms', payload);
      else await api.put(`/ecg-rhythms/${editing}`, payload);
      toast.success('Ritmo guardado');
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar/desactivar el ritmo "${r.nombre}"?`)) return;
    try {
      const res = await api.delete(`/ecg-rhythms/${r.id}`);
      toast.success(res.data.message); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-6" data-testid="ecg-rhythms-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Activity className="w-6 h-6 text-emerald-500" /> Ritmos ECG</h1>
          <p className="text-sm text-slate-500">Mantenedor de ritmos disponibles para el monitor clínico, escenarios y supervisión.</p>
        </div>
        <Button onClick={openNew} data-testid="new-rhythm-btn"><Plus size={16} className="mr-1" /> Nuevo ritmo</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ritmos configurados ({rhythms.length})</CardTitle>
          <CardDescription className="text-xs">Los ritmos de sistema no se eliminan: se desactivan. El rango de FC se usa para las advertencias de coherencia clínica.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-8" /> : (
            <table className="w-full text-sm" data-testid="rhythms-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2 pr-2">Código</th><th className="py-2 pr-2">Nombre</th>
                  <th className="py-2 pr-2">Regular</th><th className="py-2 pr-2">QRS</th>
                  <th className="py-2 pr-2">FC habitual</th><th className="py-2 pr-2">Tipo</th><th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rhythms.map(r => (
                  <tr key={r.id} className="border-b last:border-0" data-testid={`rhythm-row-${r.codigo}`}>
                    <td className="py-2 pr-2 font-mono text-xs">{r.codigo}</td>
                    <td className="py-2 pr-2">{r.nombre}</td>
                    <td className="py-2 pr-2">{r.regular ? 'Sí' : 'No'}</td>
                    <td className="py-2 pr-2 capitalize">{r.qrs}</td>
                    <td className="py-2 pr-2">{r.fc_max > 0 ? `${r.fc_min}-${r.fc_max} lpm` : '—'}</td>
                    <td className="py-2 pr-2">{r.es_sistema ? <Badge variant="outline" className="text-[10px]">Sistema</Badge> : <Badge className="bg-cyan-100 text-cyan-700 text-[10px]">Personalizado</Badge>}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-rhythm-${r.codigo}`}><Pencil size={14} /></Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(r)} data-testid={`delete-rhythm-${r.codigo}`}><Trash2 size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent data-testid="rhythm-dialog">
          <DialogHeader><DialogTitle>{editing === 'new' ? 'Nuevo ritmo ECG' : 'Editar ritmo'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Código</Label>
              <Input value={form.codigo} disabled={editing !== 'new'} data-testid="rhythm-codigo-input"
                onChange={e => setForm(f => ({ ...f, codigo: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} />
            </div>
            <div className="space-y-1">
              <Label>Nombre</Label>
              <Input value={form.nombre} data-testid="rhythm-nombre-input"
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Regularidad</Label>
              <select className="w-full border rounded-lg px-2 py-2 text-sm" value={form.regular ? '1' : '0'}
                onChange={e => setForm(f => ({ ...f, regular: e.target.value === '1' }))}>
                <option value="1">Regular</option><option value="0">Irregular</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>Complejo QRS</Label>
              <select className="w-full border rounded-lg px-2 py-2 text-sm" value={form.qrs}
                onChange={e => setForm(f => ({ ...f, qrs: e.target.value }))}>
                <option value="angosto">Angosto</option><option value="ancho">Ancho</option>
                <option value="variable">Variable</option><option value="ausente">Ausente</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>FC mínima habitual</Label>
              <Input type="number" value={form.fc_min} onChange={e => setForm(f => ({ ...f, fc_min: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>FC máxima habitual</Label>
              <Input type="number" value={form.fc_max} onChange={e => setForm(f => ({ ...f, fc_max: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="save-rhythm-btn">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
